import { Card, Progress, Typography, Button, Space, Alert } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  FileOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { UploadItem } from "@/hooks/useUpload";
import { formatFileSize } from "@/utils/formatters";
import { DOCUMENT_AUTO_DELETE_DAYS } from "@/utils/constants";

const { Text } = Typography;

interface UploadProgressProps {
  uploads: UploadItem[];
  onCancel: (uploadId: string) => void;
  onClearAll: () => void;
}

export default function UploadProgress({
  uploads,
  onCancel,
  onClearAll,
}: UploadProgressProps) {
  if (uploads.length === 0) {
    return null;
  }

  const activeCount = uploads.filter(
    (u) => u.status === "pending" || u.status === "uploading"
  ).length;
  const completedCount = uploads.filter((u) => u.status === "completed").length;
  const failedCount = uploads.filter((u) => u.status === "error").length;

  const getStatusIcon = (status: UploadItem["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircleFilled style={{ color: "#3B6D11" }} />;
      case "error":
        return <CloseCircleFilled style={{ color: "#A32D2D" }} />;
      default:
        return <FileOutlined style={{ color: "#8c8c8c" }} />;
    }
  };

  const renderTitle = () => {
    if (activeCount > 0) {
      return `Uploading ${completedCount + 1} of ${uploads.length} files...`;
    }
    if (failedCount > 0 && completedCount > 0) {
      return `${completedCount} uploaded, ${failedCount} failed`;
    }
    if (failedCount > 0) {
      return `${failedCount} upload${failedCount > 1 ? "s" : ""} failed`;
    }
    return `${completedCount} file${completedCount > 1 ? "s" : ""} uploaded`;
  };

  return (
    <Card
      className="upload-progress-card"
      size="small"
      title={
        <Space>
          <Text strong>{renderTitle()}</Text>
        </Space>
      }
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClearAll}
        />
      }
      styles={{
        body: {
          maxHeight: 300,
          overflow: "auto",
          padding: "8px 12px",
        },
      }}
    >
      {uploads.map((upload) => (
        <div
          key={upload.id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 0",
            borderBottom:
              uploads.indexOf(upload) < uploads.length - 1
                ? "1px solid rgba(0,0,0,0.06)"
                : "none",
          }}
        >
          {getStatusIcon(upload.status)}

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              ellipsis
              style={{ display: "block", fontSize: 13 }}
              title={upload.file.name}
            >
              {upload.file.name}
            </Text>

            {(upload.status === "pending" || upload.status === "uploading") && (
              <Progress
                percent={upload.progress}
                size="small"
                showInfo={false}
                strokeColor="#0F6E56"
              />
            )}

            {upload.status === "completed" && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {formatFileSize(upload.file.size)}
              </Text>
            )}

            {upload.status === "error" && (
              <Text type="danger" style={{ fontSize: 11 }}>
                {upload.error || "Upload failed"}
              </Text>
            )}
          </div>

          {(upload.status === "pending" || upload.status === "uploading") && (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => onCancel(upload.id)}
            />
          )}
        </div>
      ))}

      {/* Auto-deletion notice when uploads complete */}
      {activeCount === 0 && completedCount > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<ClockCircleOutlined />}
          message={`Documents will auto-delete in ${DOCUMENT_AUTO_DELETE_DAYS} days`}
          style={{ marginTop: 12, fontSize: 12 }}
        />
      )}
    </Card>
  );
}
